import { Button, Fade, Stack, Typography, useTheme } from "@mui/material";
import { ControlledBigTooltip } from "components/ControlledBigTooltip";
import { useState } from "react";

import { AttentionItem, AttentionKind } from "./attention";
import { useAttentionTravel } from "./AttentionTargets";

/**
 * How each reason reads as a heading, and the order the reviewer meets them.
 *
 * Validation first, because it is the only one saying a value is wrong rather
 * than worth a look. The rest are ordered by how much is at stake: a row that
 * would disappear, a value that would replace another, a reading nobody has
 * checked.
 */
const SECTIONS: readonly { kind: AttentionKind; title: string }[] = [
  { kind: "issue", title: "Fix before applying" },
  { kind: "recordRow", title: "On the record, not on this document" },
  { kind: "recordConflict", title: "Would replace what the record holds" },
  { kind: "lowConfidence", title: "Worth checking" },
];

/**
 * Everything wanting the reviewer's attention, in one control beside Confirm.
 *
 * Three lists were three answers to "what is left?" — the validation issues, the
 * record differences, and the flagged readings — each with its own button, its own
 * count and its own colour, none of which added up to a number the reviewer could
 * work down. They all came from one walk already (`listAttention`), so this is
 * that walk rendered once, grouped by reason, every row travelling to the control
 * it names.
 *
 * Error-coloured only when a validation issue is present. The other three are
 * questions for the reviewer rather than rules they have broken, and colouring
 * them red said otherwise.
 */
export function AttentionListButton({ attention }: { attention: readonly AttentionItem[] }) {
  const theme = useTheme();
  const { travelTo } = useAttentionTravel();
  const [open, setOpen] = useState(false);

  if (attention.length === 0) return null;

  const hasIssue = attention.some((item) => item.kind === "issue");
  // Counted by control, not by reason: a field flagged for two reasons is one
  // place to go and one thing to settle, so counting the reasons promised a
  // number that pressing Jump to next could never work down. An issue with
  // nowhere to travel to still counts — it is on the list and still has to be
  // dealt with.
  // Keyed by position for an item with nowhere to travel to: two issues that
  // match no control and read the same are still two things to deal with, and
  // keying them by their text collapsed them into one.
  const stops = new Set(attention.map((item, index) => item.path || `orphan:${index}`)).size;
  const label = `${stops} to resolve`;

  return (
    <ControlledBigTooltip
      open={open}
      onClose={() => setOpen(false)}
      placement="top-end"
      // The theme makes every tooltip non-interactive, which puts
      // `pointer-events: none` on the popper and would drop every click through
      // to the form behind it.
      disableInteractive={false}
      slots={{ transition: Fade }}
      slotProps={{ tooltip: { sx: { maxWidth: theme.spacing(56) } } }}
      title={
        <Stack gap={1.25} sx={{ maxHeight: theme.spacing(75), overflowY: "auto" }}>
          {SECTIONS.map(({ kind, title }) => {
            const rows = attention.filter((item) => item.kind === kind);
            if (rows.length === 0) return null;
            return (
              <Stack key={kind} gap={0.5}>
                <Typography variant="subtitle1" fontWeight="bold" fontSize="12px">
                  {title}
                </Typography>
                <Stack component="ul" gap={0.5} sx={{ pl: 2, m: 0 }}>
                  {rows.map((row, index) => (
                    <li key={`${row.kind}:${row.path}:${row.label}:${row.detail ?? ""}:${index}`}>
                      <Typography
                        component={row.path === "" ? "span" : "button"}
                        type={row.path === "" ? undefined : "button"}
                        variant="caption"
                        onClick={
                          row.path === ""
                            ? undefined
                            : () => {
                                setOpen(false);
                                travelTo(row.path);
                              }
                        }
                        sx={{
                          appearance: "none",
                          border: "none",
                          background: "none",
                          p: 0,
                          m: 0,
                          font: "inherit",
                          fontSize: "12px",
                          color: "inherit",
                          textAlign: "left",
                          // An issue keyed to nothing on this form has no control
                          // to travel to, so it reads as text rather than
                          // pretending to be a link.
                          cursor: row.path === "" ? "default" : "pointer",
                          ...(row.path === ""
                            ? {}
                            : { "&:hover": { textDecoration: "underline" } }),
                        }}
                      >
                        {row.detail == null ? row.label : `${row.label} — ${row.detail}`}
                      </Typography>
                    </li>
                  ))}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      }
    >
      <Button
        type="button"
        color={hasIssue ? "error" : "warning"}
        variant="text"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {label}
      </Button>
    </ControlledBigTooltip>
  );
}
